import { Box, Link, Stack } from '@mui/material';
import { EditorContent, useEditor } from '@tiptap/react';
import type { MouseEvent } from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { toEditorHtml } from '../state/detailsView';

import { DESCRIPTION_CONTENT_SX, DESCRIPTION_EXTENSIONS } from './extensions';

/**
 * The description as What it's about shows it (brief §3): the editor's own
 * renderer, read-only — so the view parses the stored HTML through the same
 * schema as the editor, and nothing in it is ever injected as raw HTML.
 *
 * Clamped to four whole lines with Show more. The line clamp cuts on a line
 * boundary across paragraphs and list items, so it never shows a half line
 * (§5), whatever the window's height.
 */
const CLAMP_LINES = 4;

export const RichTextView = ({ value }: { value: string }) => {
  const { t } = useTranslation('pmDetails');
  const ref = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const editor = useEditor({
    extensions: DESCRIPTION_EXTENSIONS,
    content: toEditorHtml(value),
    editable: false,
    immediatelyRender: true,
  });

  useEffect(() => {
    if (editor && !editor.isDestroyed) editor.commands.setContent(toEditorHtml(value));
  }, [editor, value]);

  // Whether the clamp hides anything — measured, since it depends on the width.
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element || expanded) return undefined;
    const measure = () => setOverflows(element.scrollHeight > element.clientHeight + 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [value, expanded, editor]);

  return (
    <Stack sx={{ alignItems: 'flex-start', gap: 0.5 }}>
      <Box
        ref={ref}
        data-automation-id="details-about-description"
        // A link inside opens itself, not the card's form.
        onClick={(event: MouseEvent) => {
          if ((event.target as HTMLElement).closest('a')) event.stopPropagation();
        }}
        sx={{
          ...DESCRIPTION_CONTENT_SX,
          ...(expanded
            ? {}
            : {
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: String(CLAMP_LINES),
                overflow: 'hidden',
              }),
        }}
      >
        <EditorContent editor={editor} />
      </Box>
      {overflows || expanded ? (
        <Link
          component="button"
          type="button"
          onClick={(event: MouseEvent) => {
            // Inside a clickable card: Show more must not also open the form.
            event.stopPropagation();
            setExpanded((open) => !open);
          }}
          aria-expanded={expanded}
          sx={{ font: 'inherit', fontSize: 'var(--font-size-body-regular)' }}
          data-automation-id="details-about-show-more"
        >
          {expanded ? t('about.ShowLess') : t('about.ShowMore')}
        </Link>
      ) : null}
    </Stack>
  );
};
